import React, { useState, useEffect } from "react";
import { api } from "../api";
import Icon from "./Icon";

import { StatusBadge, PriorityBadge } from "./Badge";
import Skeleton from "./Skeleton";
import { EmptyState } from "./State";
import Modal from "./Modal";

const STATUS_TRANSITIONS = {
  open: ["in_progress"],
  in_progress: ["resolved", "open"],
  resolved: ["closed", "in_progress"],
  closed: [],
  deleted: [],
};

const STATUS_LABELS = {
  open: "Open",
  in_progress: "In progress",
  resolved: "Resolved",
  closed: "Closed",
  deleted: "Deleted",
};

export function FormattedDescription({ ticket, text }) {
  const rawText = text || ticket?.ticket_description || ticket?.description;
  if (!rawText && !ticket?.parsed_summary) return null;

  let cleanSummary = ticket?.parsed_summary;
  let metaItems = [];

  if (ticket?.parsed_metadata && Object.keys(ticket.parsed_metadata).length > 0) {
    metaItems = Object.entries(ticket.parsed_metadata).map(([key, value]) => ({
      key,
      value,
    }));
  } else if (rawText) {
    const kvRegex = /\*\*(.*?):\*\*\s*([^\n*]+)/g;
    let match;
    while ((match = kvRegex.exec(rawText)) !== null) {
      metaItems.push({ key: match[1].trim(), value: match[2].trim() });
    }
    cleanSummary = rawText.replace(/\*\*(.*?):\*\*\s*([^\n*]+)/g, "").trim();
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
      {cleanSummary && (
        <div
          style={{
            fontSize: 13,
            color: "#374151",
            lineHeight: 1.5,
            fontWeight: 500,
            background: "#F8FAFC",
            border: "1px solid #E2E8F0",
            borderRadius: 7,
            padding: "8px 12px",
          }}
        >
          {cleanSummary}
        </div>
      )}

      {metaItems.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {metaItems.map((item, idx) => (
            <div
              key={idx}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                background: "#EFF6FF",
                border: "1px solid #DBEAFE",
                borderRadius: 6,
                padding: "3px 8px",
                fontSize: 11,
              }}
            >
              <span style={{ color: "#1D4ED8", fontWeight: 600 }}>{item.key}:</span>
              <span
                style={{
                  color: "#1E293B",
                  fontWeight: 600,
                  fontFamily: item.key.toLowerCase().includes("id") ? "monospace" : undefined,
                }}
              >
                {item.value}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function RCAPanel({ rca }) {
  const [expandedLogs, setExpandedLogs] = useState({});

  const toggleLogs = (idx) => {
    setExpandedLogs((prev) => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  const confidence = rca.confidence_score
    ? Math.round(rca.confidence_score * 100)
    : null;
  const confColor =
    confidence >= 75 ? "#16A34A" : confidence >= 50 ? "#CA8A04" : "#DC2626";
  const confBg =
    confidence >= 75 ? "#F0FDF4" : confidence >= 50 ? "#FEFCE8" : "#FEF2F2";

  // Parse RAG snippet
  const parseRagSnippet = (snippet) => {
    const parts = {
      type: "Historical Context",
      title: "Similar Incident",
      description: "",
      rootCause: "",
      resolution: "",
    };

    if (!snippet) return parts;

    const firstLine = snippet.split("\n")[0] || "";
    const typeMatch = firstLine.match(/^\[(.*?)\]/);
    if (typeMatch) parts.type = typeMatch[1];

    const titleMatch = firstLine.match(/Title:\s*(.*)$/);
    if (titleMatch) parts.title = titleMatch[1];

    const descMatch = snippet.match(
      /Description:\s*([\s\S]*?)(?=Root Cause:|Resolution:|$)/
    );
    if (descMatch) parts.description = descMatch[1].trim();

    const causeMatch = snippet.match(
      /Root Cause:\s*([\s\S]*?)(?=Resolution:|$)/
    );
    if (causeMatch) parts.rootCause = causeMatch[1].trim();

    const resMatch = snippet.match(/Resolution:\s*([\s\S]*?)$/);
    if (resMatch) parts.resolution = resMatch[1].trim();

    return parts;
  };

  const allFindings = rca.evidence?.all_findings || [];
  const ragSnippets = rca.evidence?.rag_snippets || [];


  const getSpecialistIcon = (name) => {
    const n = String(name).toLowerCase();
    if (n.includes("network")) return "network";
    if (n.includes("db") || n.includes("database")) return "database";
    if (n.includes("system") || n.includes("server") || n.includes("host"))
      return "server";
    return "zap";
  };

  const getDomainTheme = (type) => {
    const t = String(type).toLowerCase();
    if (t.includes("network")) {
      return {
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#DBEAFE",
        icon: "network"
      };
    }
    if (t.includes("db") || t.includes("database")) {
      return {
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#EDE9FE",
        icon: "database"
      };
    }
    if (t.includes("system") || t.includes("server") || t.includes("host")) {
      return {
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FEF3C7",
        icon: "server"
      };
    }
    return {
      color: "#0D9488",
      bg: "#F0FDFA",
      border: "#CCFBF1",
      icon: "activity"
    };
  };

  const renderMetricCell = (key, value) => {
    let displayLabel = key.replace(/_/g, " ");
    let displayValue = "";
    let unit = "";

    // Extract unit suffixes
    if (key.endsWith("_ms")) {
      displayLabel = key.slice(0, -3).replace(/_/g, " ");
      unit = "ms";
    } else if (key.endsWith("_pct")) {
      displayLabel = key.slice(0, -4).replace(/_/g, " ");
      unit = "%";
    } else if (key.endsWith("_sec")) {
      displayLabel = key.slice(0, -4).replace(/_/g, " ");
      unit = "/s";
    } else if (key.endsWith("_connections")) {
      displayLabel = key.slice(0, -12).replace(/_/g, " ");
      unit = " connections";
    }

    displayLabel = displayLabel
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

    if (typeof value === "number") {
      displayValue = value % 1 === 0 ? value.toLocaleString() : value.toFixed(2);
    } else {
      displayValue = String(value);
    }

    return (
      <div
        key={key}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          background: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: 6,
          padding: "8px 10px",
          boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.02)",
        }}
      >
        <span
          style={{
            fontSize: 10,
            color: "#64748B",
            fontWeight: 500,
            textTransform: "capitalize",
          }}
        >
          {displayLabel}
        </span>
        <span
          style={{
            fontSize: 13,
            color: "#0F172A",
            fontWeight: 600,
          }}
        >
          {displayValue}
          {unit && (
            <span style={{ fontSize: 11, fontWeight: 500, color: "#64748B", marginLeft: 2 }}>
              {unit}
            </span>
          )}
        </span>
      </div>
    );
  };

  const highlightLogLine = (line) => {
    if (!line) return "";
    const hasError = line.includes("[ERROR]") || line.includes("level=error") || line.includes("error") || line.includes("i/o timeout");
    const hasWarn = line.includes("[WARN]") || line.includes("level=warn") || line.includes("warn");
    const hasInfo = line.includes("[INFO]") || line.includes("level=info") || line.includes("info");

    let color = "#E2E8F0";
    if (hasError) color = "#FDA4AF"; // light red/rose
    else if (hasWarn) color = "#FDE047"; // light yellow
    else if (hasInfo) color = "#93C5FD"; // light blue

    return (
      <div style={{ color, marginBottom: 2, lineHeight: 1.5 }}>
        {line}
      </div>
    );
  };

  const renderMarkdown = (text) => {
    if (!text) return null;
    const lines = text.split("\n");
    const renderedElements = [];
    let currentList = [];

    const flushList = (key) => {
      if (currentList.length > 0) {
        renderedElements.push(
          <div key={key} style={{ display: "flex", flexDirection: "column", gap: 8, margin: "8px 0" }}>
            {currentList}
          </div>
        );
        currentList = [];
      }
    };

    for (let idx = 0; idx < lines.length; idx++) {
      const line = lines[idx].trim();
      if (!line) continue;

      if (line.startsWith("###")) {
        flushList(`list-${idx}`);
        const headerText = line.replace(/^###\s*/, "");
        renderedElements.push(
          <div
            key={`header-${idx}`}
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#1E293B",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginTop: 14,
              marginBottom: 6,
              display: "flex",
              alignItems: "center",
              gap: 6
            }}
          >
            <span style={{ width: 4, height: 12, background: "#10B981", borderRadius: 2 }} />
            {headerText}
          </div>
        );
      } else if (/^\d+\.\s+/.test(line)) {
        const itemText = line.replace(/^\d+\.\s+/, "");
        const parts = itemText.split(/\*\*(.*?)\*\*/g);
        const content = parts.map((part, pIdx) => {
          if (pIdx % 2 === 1) {
            return <strong key={pIdx} style={{ color: "#0F172A", fontWeight: 600 }}>{part}</strong>;
          }
          return part;
        });

        const stepNum = line.match(/^(\d+)\.\s+/)[1];

        currentList.push(
          <div
            key={`step-${idx}`}
            style={{
              display: "flex",
              gap: 12,
              background: "#F8FAFC",
              border: "1px solid #E2E8F0",
              borderRadius: 6,
              padding: "10px 12px",
              alignItems: "flex-start"
            }}
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 20,
                height: 20,
                borderRadius: "50%",
                background: "#D1FAE5",
                color: "#065F46",
                fontSize: 10,
                fontWeight: 700,
                flexShrink: 0,
                marginTop: 1
              }}
            >
              {stepNum}
            </span>
            <span style={{ fontSize: 12.5, color: "#334155", lineHeight: 1.5 }}>
              {content}
            </span>
          </div>
        );
      } else {
        flushList(`list-${idx}`);
        const parts = line.split(/\*\*(.*?)\*\*/g);
        const content = parts.map((part, pIdx) => {
          if (pIdx % 2 === 1) {
            return <strong key={pIdx} style={{ color: "#0F172A", fontWeight: 600 }}>{part}</strong>;
          }
          return part;
        });

        renderedElements.push(
          <p key={`p-${idx}`} style={{ fontSize: 12.5, color: "#475569", lineHeight: 1.6, margin: "6px 0" }}>
            {content}
          </p>
        );
      }
    }

    flushList("list-final");
    return renderedElements;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Badges / Meta */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <StatusBadge status={rca.status || "unknown"} />
        {confidence !== null && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "3px 9px",
              borderRadius: 20,
              background: confBg,
              color: confColor,
              fontSize: 11,
              fontWeight: 600,
              border: `1px solid ${confidence >= 75 ? "#D1FAE5" : confidence >= 50 ? "#FEF08A" : "#FEE2E2"}`,
            }}
          >
            <div
              style={{
                width: 32,
                height: 6,
                background: "#E2E8F0",
                borderRadius: 3,
                overflow: "hidden",
                display: "inline-block",
              }}
            >
              <div
                style={{
                  width: `${confidence}%`,
                  height: "100%",
                  background: confColor,
                  borderRadius: 3,
                }}
              />
            </div>
            <span>{confidence}% Confidence</span>
          </div>
        )}
        {rca.escalated && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "3px 9px",
              borderRadius: 20,
              background: "#FEF2F2",
              color: "#DC2626",
              fontSize: 11,
              fontWeight: 600,
              border: "1px solid #FEE2E2",
            }}
          >
            <Icon name="alert" size={10} color="#DC2626" /> Escalated
          </span>
        )}
      </div>

      {/* Root Cause Card */}
      {rca.root_cause && (
        <div
          style={{
            background: "linear-gradient(135deg, #F8FAFC 0%, #EFF6FF 100%)",
            border: "1px solid #DBEAFE",
            borderLeft: "4px solid #2563EB",
            borderRadius: 8,
            padding: "16px",
            boxShadow: "0 1px 3px 0 rgba(37, 99, 235, 0.04)",
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "#1E3A8A",
              textTransform: "uppercase",
              letterSpacing: "0.07em",
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Icon name="search" size={12} color="#2563EB" />
            Root Cause Diagnosis
          </div>
          <p
            style={{
              margin: 0,
              fontSize: 13,
              color: "#0F172A",
              lineHeight: 1.6,
              fontWeight: 500,
            }}
          >
            {rca.root_cause}
          </p>
        </div>
      )}

      {/* Specialist Analyses */}
      {allFindings.length > 0 && (
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#475569",
              textTransform: "uppercase",
              letterSpacing: "0.07em",
              marginBottom: 10,
            }}
          >
            Domain Specialist Insights
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {allFindings.map((finding, idx) => {
              const specConfidence = finding.domain_confidence
                ? Math.round(finding.domain_confidence * 100)
                : null;
              const hasMetrics =
                finding.collected_metrics &&
                Object.keys(finding.collected_metrics).length > 0;
              const hasLogs =
                finding.extracted_logs && finding.extracted_logs.length > 0;

              return (
                <div
                  key={idx}
                  style={{
                    background: "#FFFFFF",
                    border: "1px solid #E2E8F0",
                    borderRadius: 8,
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.02)",
                  }}
                >
                  {/* Specialist Header */}
                  <div
                    style={{
                      background: "#F8FAFC",
                      padding: "12px 14px",
                      borderBottom: "1px solid #E2E8F0",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontWeight: 600,
                        fontSize: 13,
                        color: "#1E293B",
                        textTransform: "capitalize",
                      }}
                    >
                      <Icon
                        name={getSpecialistIcon(finding.specialist_name)}
                        size={14}
                        color="#2563EB"
                      />
                      {finding.specialist_name} Specialist Findings
                    </div>
                    {specConfidence && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#1D4ED8",
                          background: "#EFF6FF",
                          border: "1px solid #DBEAFE",
                          padding: "2px 8px",
                          borderRadius: 12,
                        }}
                      >
                        {specConfidence}% Match
                      </span>
                    )}
                  </div>

                  {/* Specialist Body */}
                  <div
                    style={{
                      padding: 14,
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                    }}
                  >
                    {/* Findings list */}
                    {finding.findings && finding.findings.length > 0 && (
                      <ul
                        style={{
                          margin: 0,
                          paddingLeft: 20,
                          fontSize: 12.5,
                          color: "#334155",
                          lineHeight: 1.6,
                        }}
                      >
                        {finding.findings.map((f, fIdx) => (
                          <li key={fIdx} style={{ marginBottom: 4 }}>
                            {f}
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Metrics Section */}
                    {hasMetrics && (
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            color: "#94A3B8",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            marginBottom: 6,
                          }}
                        >
                          Collected Metrics
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
                            gap: 8,
                          }}
                        >
                          {Object.entries(finding.collected_metrics).map(([k, v]) =>
                            renderMetricCell(k, v)
                          )}
                        </div>
                      </div>
                    )}

                    {/* Logs Section */}
                    {hasLogs && (
                      <div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 6,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 10,
                              color: "#94A3B8",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                            }}
                          >
                            Extracted Logs ({finding.extracted_logs.length})
                          </span>
                          <button
                            onClick={() => toggleLogs(idx)}
                            style={{
                              fontSize: 11,
                              color: "#2563EB",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: 0,
                            }}
                          >
                            {expandedLogs[idx] ? "Collapse" : "Expand"}
                          </button>
                        </div>
                        <div
                          style={{
                            background: "#0F172A",
                            borderRadius: 6,
                            padding: "10px 12px",
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                            fontSize: 11,
                            color: "#E2E8F0",
                            maxHeight: expandedLogs[idx] ? "none" : 88,
                            overflowY: expandedLogs[idx] ? "visible" : "hidden",
                            border: "1px solid #1E293B",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-all",
                          }}
                        >
                          {finding.extracted_logs.map((line, lIdx) =>
                            highlightLogLine(line)
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Suggested Steps */}
      {rca.suggestions && rca.suggestions.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#475569",
              textTransform: "uppercase",
              letterSpacing: "0.07em",
            }}
          >
            Recommended Next Steps
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {rca.suggestions.map((suggestion, idx) => {
              const theme = getDomainTheme(suggestion.suggestion_type);
              return (
                <div
                  key={idx}
                  style={{
                    background: "#FFFFFF",
                    border: `1px solid ${theme.border}`,
                    borderRadius: 8,
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.02)",
                  }}
                >
                  {/* Step Header */}
                  <div
                    style={{
                      background: theme.bg,
                      padding: "10px 12px",
                      borderBottom: `1px solid ${theme.border}`,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span
                        style={{
                          background: theme.color,
                          color: "#FFFFFF",
                          fontSize: 10,
                          fontWeight: 700,
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {idx + 1}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: theme.color, textTransform: "uppercase" }}>
                        {suggestion.suggestion_type ? suggestion.suggestion_type.replace(/_/g, " ") : "remediation"} Actions
                      </span>
                    </div>
                  </div>

                  {/* Step Body */}
                  <div style={{ padding: "12px 14px" }}>
                    {renderMarkdown(suggestion.suggestion_text)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Knowledge Base Snippets */}
      {ragSnippets.length > 0 && (
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#475569",
              textTransform: "uppercase",
              letterSpacing: "0.07em",
              marginBottom: 10,
            }}
          >
            Correlated KB & Runbooks
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {ragSnippets.map((snippet, idx) => {
              const parsed = parseRagSnippet(snippet);
              return (
                <div
                  key={idx}
                  style={{
                    background: "#FFFFFF",
                    border: "1px solid #E2E8F0",
                    borderRadius: 8,
                    padding: 12,
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.02)",
                  }}
                >
                  <div style={{ display: "flex", justifyBetween: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
                    <div>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: "#64748B",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          marginBottom: 2,
                        }}
                      >
                        {parsed.type}
                      </div>
                      <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#1E293B" }}>
                        {parsed.title}
                      </h4>
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 12, color: "#475569" }}>
                    {parsed.description && (
                      <p style={{ margin: 0, lineHeight: 1.5 }}>
                        <strong>Symptoms:</strong> {parsed.description}
                      </p>
                    )}
                    {parsed.rootCause && (
                      <p style={{ margin: 0, lineHeight: 1.5 }}>
                        <strong>Historical Cause:</strong> {parsed.rootCause}
                      </p>
                    )}
                    {parsed.resolution && (
                      <p
                        style={{
                          margin: 0,
                          lineHeight: 1.5,
                          background: "#ECFDF5",
                          border: "1px solid #A7F3D0",
                          borderRadius: 6,
                          padding: "6px 8px",
                          color: "#065F46",
                        }}
                      >
                        <strong>Resolution Steps:</strong> {parsed.resolution}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Fallback empty state */}
      {!rca.root_cause && !rca.suggestions?.length && allFindings.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: 20,
            color: "#9CA3AF",
            fontSize: 13,
          }}
        >
          Analysis pending or incomplete.
        </div>
      )}
    </div>
  );
}

export function IncidentsPanel({ incidents }) {
  if (!incidents.length)
    return (
      <EmptyState
        icon="activity"
        title="No linked incidents"
        subtitle="No incidents linked to this ticket."
      />
    );

  const SEV_STYLES = {
    critical: { bg: "#FEF2F2", border: "#FEE2E2", text: "#991B1B", indicator: "#EF4444" },
    high: { bg: "#FFF7ED", border: "#FED7AA", text: "#9A3412", indicator: "#F97316" },
    medium: { bg: "#FEFCE8", border: "#FEF08A", text: "#713F12", indicator: "#EAB308" },
    low: { bg: "#F0FDF4", border: "#DCFCE7", text: "#166534", indicator: "#22C55E" },
  };

  const STATE_STYLES = {
    detected: { bg: "#EFF6FF", text: "#1E40AF", border: "#DBEAFE" },
    active: { bg: "#EFF6FF", text: "#1E40AF", border: "#DBEAFE" },
    suppressed: { bg: "#F3F4F6", text: "#374151", border: "#E5E7EB" },
    resolved: { bg: "#ECFDF5", text: "#065F46", border: "#D1FAE5" },
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {incidents.map((inc) => {
        const sevStyle = SEV_STYLES[inc.severity?.toLowerCase()] || {
          bg: "#F9FAFB",
          border: "#E5E7EB",
          text: "#374151",
          indicator: "#9CA3AF"
        };
        const stateStyle = STATE_STYLES[inc.state?.toLowerCase()] || {
          bg: "#F9FAFB",
          text: "#374151",
          border: "#E5E7EB"
        };

        return (
          <div
            key={inc.id}
            style={{
              background: "#FFFFFF",
              border: "1px solid #E2E8F0",
              borderLeft: `4px solid ${sevStyle.indicator}`,
              borderRadius: 8,
              padding: "12px 14px",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.02)",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Icon name="activity" size={13} color="#64748B" />
                <span
                  style={{
                    fontFamily: "SFMono-Regular, Consolas, monospace",
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#475569",
                  }}
                >
                  INC-{inc.id.slice(0, 8).toUpperCase()}
                </span>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {/* Severity Badge */}
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    background: sevStyle.bg,
                    color: sevStyle.text,
                    border: `1px solid ${sevStyle.border}`,
                    padding: "2px 8px",
                    borderRadius: 12,
                    textTransform: "capitalize",
                  }}
                >
                  {inc.severity}
                </span>
                {/* State Badge */}
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    background: stateStyle.bg,
                    color: stateStyle.text,
                    border: `1px solid ${stateStyle.border}`,
                    padding: "2px 8px",
                    borderRadius: 12,
                    textTransform: "capitalize",
                  }}
                >
                  {inc.state}
                </span>
              </div>
            </div>

            {/* Content Details */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: 8,
                fontSize: 11.5,
                color: "#64748B",
                borderTop: "1px solid #F1F5F9",
                paddingTop: 8,
              }}
            >
              {inc.server_id && (
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <Icon name="server" size={11} color="#94A3B8" />
                  <span>
                    Server ID: <strong style={{ color: "#334155" }}>{inc.server_id.slice(0, 8)}…</strong>
                  </span>
                </div>
              )}
              {inc.detected_at && (
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <Icon name="clock" size={11} color="#94A3B8" />
                  <span>
                    Detected: <strong style={{ color: "#334155" }}>{new Date(inc.detected_at).toLocaleString()}</strong>
                  </span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function CommentsPanel({ comments }) {
  if (!comments.length)
    return (
      <EmptyState
        icon="message"
        title="No comments"
        subtitle="No comments on this ticket yet."
      />
    );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {comments.map((c) => (
        <div
          key={c.id}
          style={{
            background: c.is_internal ? "#FFFBEB" : "#F8FAFC",
            border: `1px solid ${c.is_internal ? "#FDE68A" : "#E2E8F0"}`,
            borderRadius: 8,
            padding: "10px 12px",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 7,
              alignItems: "center",
              marginBottom: 5,
            }}
          >
            <span
              style={{
                fontSize: 10,
                background: "#E5E7EB",
                padding: "2px 7px",
                borderRadius: 9,
                color: "#374151",
                fontWeight: 600,
              }}
            >
              {c.author_type}
            </span>
            {c.is_internal && (
              <span
                style={{
                  fontSize: 10,
                  background: "#FEF3C7",
                  color: "#B45309",
                  padding: "2px 7px",
                  borderRadius: 9,
                  fontWeight: 600,
                }}
              >
                Internal
              </span>
            )}
            {c.created_at && (
              <span
                style={{ fontSize: 10, color: "#9CA3AF", marginLeft: "auto" }}
              >
                {new Date(c.created_at).toLocaleString()}
              </span>
            )}
          </div>
          <p
            style={{
              margin: 0,
              fontSize: 12,
              color: "#1E293B",
              lineHeight: 1.6,
            }}
          >
            {c.body}
          </p>
        </div>
      ))}
    </div>
  );
}

export default function TicketDetailModal({ ticket, onClose, onUpdated }) {
  const [currentTicket, setCurrentTicket] = useState(ticket);
  const [rca, setRca] = useState(null);
  const [comments, setComments] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [editStatus, setEditStatus] = useState(ticket.status);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [deleteUpdating, setDeleteUpdating] = useState(false);
  const [actionError, setActionError] = useState("");

  useEffect(() => {
    setCurrentTicket(ticket);
    setEditStatus(ticket.status);
    setActionError("");
  }, [ticket]);

  useEffect(() => {
    setLoading(true);
    setRca(null);
    setComments([]);
    setIncidents([]);
    setTab("overview");
    Promise.all([
      api(`/tickets/${ticket.id}`)
        .then((data) => {
          if (data) {
            setCurrentTicket(data);
            setEditStatus(data.status);
          }
        })
        .catch(() => null),
      api(`/tickets/${ticket.id}/rca`)
        .then(setRca)
        .catch(() => null),
      api(`/tickets/${ticket.id}/comments`)
        .then(setComments)
        .catch(() => []),
      api(`/tickets/${ticket.id}/incidents`)
        .then(setIncidents)
        .catch(() => []),
    ]).finally(() => setLoading(false));
  }, [ticket.id]);

  async function handleStatusUpdate() {
    if (editStatus === currentTicket.status) return;
    const validNextStatuses = STATUS_TRANSITIONS[currentTicket.status] || [];
    if (!validNextStatuses.includes(editStatus)) {
      setActionError("That status transition is not allowed.");
      setEditStatus(currentTicket.status);
      return;
    }

    setActionError("");
    setStatusUpdating(true);
    try {
      const u = await api(`/tickets/${currentTicket.id}/status`, {
        method: "PUT",
        body: JSON.stringify({ status: editStatus }),
      });
      if (u) {
        setCurrentTicket(u);
        setEditStatus(u.status);
        onUpdated(u);
      }
    } catch {
      setActionError("Could not update ticket status. Please try again.");
      setEditStatus(currentTicket.status);
    } finally {
      setStatusUpdating(false);
    }
  }

  async function handleDeleteTicket() {
    setActionError("");
    setDeleteUpdating(true);
    try {
      await api(`/tickets/${currentTicket.id}`, {
        method: "DELETE",
      });
      const deletedTicket = { ...currentTicket, status: "deleted" };
      setCurrentTicket(deletedTicket);
      setEditStatus("deleted");
      onUpdated(deletedTicket, { deleted: true });
      onClose();
    } catch {
      setActionError("Could not delete ticket. Please try again.");
    } finally {
      setDeleteUpdating(false);
    }
  }

  const nextStatuses = STATUS_TRANSITIONS[currentTicket.status] || [];
  const canEditStatus =
    currentTicket.status !== "deleted" && nextStatuses.length > 0;
  const statusOptions = [currentTicket.status, ...nextStatuses].filter(Boolean);
  const actionPending = statusUpdating || deleteUpdating;

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "rca", label: `RCA` },
    { id: "incidents", label: `Incidents (${incidents.length})` },
    { id: "comments", label: `Comments (${comments.length})` },
  ];

  return (
    <Modal title="Ticket Detail" onClose={onClose} width={680}>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            marginBottom: 8,
            flexWrap: "wrap",
          }}
        >
          <StatusBadge status={currentTicket.status} />
          <PriorityBadge priority={currentTicket.priority} />
          <span
            style={{
              fontSize: 11,
              color: "#9CA3AF",
              background: "#F9FAFB",
              padding: "2px 8px",
              borderRadius: 10,
              border: "1px solid #F3F4F6",
            }}
          >
            via {currentTicket.source}
          </span>
        </div>
        <h2
          style={{
            margin: "0 0 6px",
            fontSize: 17,
            fontWeight: 700,
            color: "#111827",
            lineHeight: 1.4,
          }}
        >
          {currentTicket.title}
        </h2>
        <FormattedDescription
          ticket={currentTicket}
        />
      </div>

      {/* Status update */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 18,
          padding: "12px 14px",
          background: "#F8FAFC",
          borderRadius: 8,
          border: "1px solid #E2E8F0",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: "#374151",
            marginRight: 4,
            display: "flex",
            alignItems: "center",
          }}
        >
          Update status:
        </div>
        <select
          value={editStatus}
          onChange={(e) => setEditStatus(e.target.value)}
          disabled={!canEditStatus || actionPending}
          style={{
            flex: 1,
            minWidth: 160,
            padding: "6px 10px",
            border: "1px solid #E5E7EB",
            borderRadius: 6,
            fontSize: 12,
            background: canEditStatus && !actionPending ? "#fff" : "#F3F4F6",
            color: canEditStatus ? "#374151" : "#9CA3AF",
            cursor: canEditStatus && !actionPending ? "pointer" : "not-allowed",
          }}
        >
          {statusOptions.map((status) => (
            <option key={status} value={status}>
              {STATUS_LABELS[status] || status}
            </option>
          ))}
        </select>
        <button
          onClick={handleStatusUpdate}
          disabled={
            actionPending ||
            editStatus === currentTicket.status ||
            !canEditStatus
          }
          style={{
            padding: "6px 14px",
            background:
              editStatus !== currentTicket.status && canEditStatus && !actionPending
                ? "#1D4ED8"
                : "#E5E7EB",
            color:
              editStatus !== currentTicket.status && canEditStatus && !actionPending
                ? "#fff"
                : "#9CA3AF",
            border: "none",
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 500,
            cursor:
              editStatus !== currentTicket.status && canEditStatus && !actionPending
                ? "pointer"
                : "default",
          }}
        >
          {statusUpdating ? "Saving…" : "Save"}
        </button>
        <button
          onClick={handleDeleteTicket}
          disabled={actionPending || currentTicket.status === "deleted"}
          style={{
            padding: "6px 12px",
            background:
              actionPending || currentTicket.status === "deleted" ? "#F3F4F6" : "#FEF2F2",
            color:
              actionPending || currentTicket.status === "deleted" ? "#9CA3AF" : "#B91C1C",
            border: "1px solid #FECACA",
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 500,
            cursor:
              actionPending || currentTicket.status === "deleted" ? "default" : "pointer",
          }}
        >
          {deleteUpdating ? "Deleting..." : "Delete"}
        </button>
        {actionError && (
          <div
            style={{
              flexBasis: "100%",
              fontSize: 12,
              color: "#B91C1C",
              background: "#FEF2F2",
              border: "1px solid #FECACA",
              borderRadius: 6,
              padding: "7px 9px",
            }}
          >
            {actionError}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          borderBottom: "1px solid #F3F4F6",
          marginBottom: 16,
        }}
      >
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: "8px 14px",
              border: "none",
              background: "none",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: tab === t.id ? 600 : 400,
              color: tab === t.id ? "#1D4ED8" : "#6B7280",
              borderBottom:
                tab === t.id ? "2px solid #1D4ED8" : "2px solid transparent",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height={52} style={{ borderRadius: 8 }} />
          ))}
        </div>
      ) : (
        <>
          {tab === "overview" && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
              }}
            >
              {[
                { label: "Ticket ID", value: currentTicket.id, mono: true, icon: "ticket", color: "#64748B" },
                {
                  label: "Status",
                  value: <StatusBadge status={currentTicket.status} />,
                  icon: "info",
                  color: "#1D4ED8"
                },
                {
                  label: "Priority",
                  value: <PriorityBadge priority={currentTicket.priority} />,
                  icon: "alert",
                  color: "#DC2626"
                },
                { label: "Source", value: currentTicket.source, icon: "globe", color: "#0D9488" },
                {
                  label: "Created",
                  value: currentTicket.created_at
                    ? new Date(currentTicket.created_at).toLocaleString("en-IN")
                    : "—",
                  icon: "clock",
                  color: "#475569"
                },
                {
                  label: "Updated",
                  value: currentTicket.updated_at
                    ? new Date(currentTicket.updated_at).toLocaleString("en-IN")
                    : "—",
                  icon: "refresh",
                  color: "#6B7280"
                },
              ].map((f) => (
                <div
                  key={f.label}
                  style={{
                    background: "#FFFFFF",
                    border: "1px solid #E2E8F0",
                    borderRadius: 10,
                    padding: "12px 14px",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.01)",
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: "#F8FAFC",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid #F1F5F9",
                    }}
                  >
                    <Icon name={f.icon} size={15} color={f.color} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#94A3B8",
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                        marginBottom: 2,
                      }}
                    >
                      {f.label}
                    </div>
                    {typeof f.value === "string" ? (
                      <div
                        style={{
                          fontSize: 13,
                          color: "#1E293B",
                          fontFamily: f.mono ? "monospace" : undefined,
                          wordBreak: "break-all",
                          fontWeight: 500,
                        }}
                      >
                        {f.value}
                      </div>
                    ) : (
                      f.value
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {tab === "rca" &&
            (rca ? (
              <RCAPanel rca={rca} />
            ) : (
              <EmptyState
                icon="search"
                title="No RCA available"
                subtitle="Root cause analysis hasn't been run yet."
              />
            ))}
          {tab === "incidents" && <IncidentsPanel incidents={incidents} />}
          {tab === "comments" && <CommentsPanel comments={comments} />}
        </>
      )}
    </Modal>
  );
}
